import { createPlugin, matchLeaf } from '@customerio/dryad';
import type { ConditionNode, GroupNode, LeafNode } from '@customerio/dryad';
import { isRecord } from '@/utils/objects';
import { ConditionType } from './condition-types';
import type { SegmentFilter } from './wire-types';

/**
 * Wire: a bare `{ segment: { id } }` leaf or an `{ or: [{ segment }…] }`
 * group, either wrapped in `{ not }`. Ids may be numbers or strings (e.g.
 * from a deep link) and round-trip verbatim; a bare leaf normalizes to a
 * one-element or-group, the shape every producer of this wire writes.
 */
export type SegmentId = number | string;

export type SegmentsNegatableState = {
  segmentIds: SegmentId[];
  negate: boolean;
};

/**
 * The picker hands back string ids; every producer of this wire writes
 * numbers. Non-numeric ids pass through untouched.
 */
export function toSegmentId(value: string): SegmentId {
  const id = Number(value);
  return Number.isNaN(id) ? value : id;
}

/**
 * The slice of the condition builder the create-segment shortcut needs.
 * Structural rather than the full `ConditionBuilder` type so any typed
 * builder (whose format and plugin generics narrow its method signatures)
 * can be passed without widening.
 */
export type SegmentsShortcutBuilder = {
  getTree: () => GroupNode;
  getNode: (id: string) => ConditionNode | undefined;
  addNode: (parentId: string, type: typeof ConditionType.SegmentsNegatable) => string | undefined;
  updateState: <TState>(node: LeafNode<TState>, patch: Partial<TState>) => void;
};

function matchOriginatingSegmentsNode(
  builder: SegmentsShortcutBuilder,
  originatingNodeId: string | undefined,
): LeafNode<SegmentsNegatableState> | undefined {
  if (originatingNodeId == null) {
    return undefined;
  }
  return matchLeaf<SegmentsNegatableState>(builder.getNode(originatingNodeId), ConditionType.SegmentsNegatable);
}

/**
 * Two mutations, so subscribers hear two notifications: addNode's carries the
 * default empty (invalid) state before updateState's carries the final one.
 * Hosts must gate their commit on isValid, or the intermediate notification
 * serializes an empty segment group onto the wire.
 */
function appendNewSegmentsNegatableNode(builder: SegmentsShortcutBuilder, segmentId: SegmentId): void {
  const nodeId = builder.addNode(builder.getTree().id, ConditionType.SegmentsNegatable);
  if (nodeId == null) {
    return;
  }
  const node = matchLeaf<SegmentsNegatableState>(builder.getNode(nodeId), ConditionType.SegmentsNegatable);
  if (node == null) {
    return;
  }
  builder.updateState(node, { segmentIds: [segmentId] });
}

/**
 * Lands a freshly created segment from the "create a new segment" shortcut.
 * The create form only ever exists as part of the add flow — a half-built row
 * is never committed — so the net outcome is one row containing the created
 * segment, never a leftover empty one: fill the originating row in place when
 * it's a freshly-added row that's still empty, and append as a new row
 * otherwise (a populated originating row, or no originating row at all).
 */
export function appendCreatedSegmentCondition(
  builder: SegmentsShortcutBuilder,
  segmentId: SegmentId,
  originatingNodeId?: string,
): void {
  const originatingNode = matchOriginatingSegmentsNode(builder, originatingNodeId);
  if (originatingNode?.state.segmentIds.length === 0) {
    builder.updateState(originatingNode, { segmentIds: [segmentId] });
    return;
  }
  appendNewSegmentsNegatableNode(builder, segmentId);
}

function segmentLeafId(json: unknown): SegmentId | null {
  if (!isRecord(json) || !isRecord(json.segment)) {
    return null;
  }
  const { id } = json.segment;
  return typeof id === 'number' || typeof id === 'string' ? id : null;
}

function allSegmentIds(candidates: unknown[]): SegmentId[] | null {
  const ids: SegmentId[] = [];
  for (const candidate of candidates) {
    const id = segmentLeafId(candidate);
    if (id == null) {
      return null;
    }
    ids.push(id);
  }
  return ids;
}

function orGroupIds(json: unknown): SegmentId[] | null {
  if (!isRecord(json) || !Array.isArray(json.or) || json.or.length === 0) {
    return null;
  }
  return allSegmentIds(json.or);
}

function leafOrGroupIds(json: unknown): SegmentId[] | null {
  const leafId = segmentLeafId(json);
  return leafId != null ? [leafId] : orGroupIds(json);
}

/**
 * The people-filter format's group-recognizer hook declines this shape by
 * returning null; a declined group falls through to plugin claiming, so this
 * plugin parses `{ or: [{ segment }…] }` as one multi-select leaf instead of
 * an or-group of rows.
 */
export function isSegmentOrGroup(json: unknown): boolean {
  return orGroupIds(json) != null;
}

function negatedSegmentIds(json: unknown): SegmentId[] | null {
  return isRecord(json) && 'not' in json ? leafOrGroupIds(json.not) : null;
}

function parseSegmentsNegatable(json: unknown): SegmentsNegatableState | null {
  const negated = negatedSegmentIds(json);
  if (negated != null) {
    return { segmentIds: negated, negate: true };
  }
  const ids = leafOrGroupIds(json);
  if (ids == null) {
    return null;
  }
  return { segmentIds: ids, negate: false };
}

type SegmentsOrGroup = { or: SegmentFilter[] };

// People-search always writes the or-group, single id included — the shape
// the legacy producers of this wire write.
function serializeSegmentsNegatable(state: SegmentsNegatableState): SegmentsOrGroup | { not: SegmentsOrGroup } {
  const wire: SegmentsOrGroup = { or: state.segmentIds.map((id) => ({ segment: { id } })) };
  if (state.negate) {
    return { not: wire };
  }
  return wire;
}

export const peopleFilterSegmentsPlugin = createPlugin({
  type: ConditionType.SegmentsNegatable,
  label: 'Segment',
  parse: parseSegmentsNegatable,
  serialize: serializeSegmentsNegatable,
  validate: (state: SegmentsNegatableState) =>
    state.segmentIds.length === 0 ? [{ message: 'Select at least one segment.', field: 'segmentIds' }] : [],
  createDefault: (): SegmentsNegatableState => ({ segmentIds: [], negate: false }),
});
